import { useState, useEffect, useCallback } from 'react'
import {
  getRequest, bodyRequest, patchRequest, deleteRequest, pathname
} from '@helpers/javascript/javascript'

export default function useProductPrices({ clientId, page, dataPath, filterProducts = [] }) {
  // existing list / edit state
  const [prices,    setPrices]    = useState([])
  const [loading,   setLoading]   = useState(true)

  // add‐flow
  const [isAdding,  setIsAdding]  = useState(false)
  const [template,  setTemplate]  = useState(null)

  // edit‐flow
  const [editingId,    setEditingId]    = useState(null)
  const [editValue,    setEditValue]    = useState('')
  const [editDiscount, setEditDiscount] = useState('')

  const productsKey = filterProducts.map(p => p.id).join(',')

  // 1. load list
  const load = useCallback(() => {
    setLoading(true)

    const params = new URLSearchParams()
    params.append('page', page)
    params.append('product_prices_filter_form[client]', clientId)

    // append each product filter
    filterProducts.forEach(p => {
      params.append('product_prices_filter_form[product][]', p.id)
    })

    getRequest(`${dataPath}.json?${params.toString()}`)
      .then(setPrices)
      .catch(error => console.error('Error loading product prices', error))
      .finally(() => setLoading(false))
  }, [clientId, page, dataPath, productsKey])

  useEffect(load, [load])


  // 2. startAdd only shows form, no API call
  const startAdd = useCallback(() => {
    setIsAdding(true)
    setTemplate(null)
  }, [])

  const cancelAdd = useCallback(() => {
    setIsAdding(false)
    setTemplate(null)
  }, [])


  // 3. loadTemplate(productId) called *after* user picks a product
  const loadTemplate = useCallback((productId) => {
    if (!productId) return
    const params = {
      'price[client_id]':  clientId,
      'price[product_id]': productId
    }
    const url = pathname({ path:`${dataPath}/new`, params, format:'json' })
    getRequest(url)
      .then(setTemplate)
      .catch(error => console.error('Error loading price for product', error))
  }, [clientId, dataPath])


  // 4. create uses the fetched template
  const create = useCallback((salePrice, discount) => {
    if (!template?.product?.id) return

    const body = {
      page,
      client_id:        clientId,
      product_id:       template.product.id,
      sale_price_cents: Math.round(salePrice * 100),
      discount:         discount === '' || discount == null ? 0 : Number(discount),
    }

    setLoading(true)
    bodyRequest({ url: pathname({path:dataPath,format:'json'}), body })
      .then(() => { cancelAdd(); load() })
      .catch(error => console.error('Error creating product-price', error))
  }, [clientId, page, dataPath, template, cancelAdd, load])


  // 5. edit flow
  const startEdit = useCallback(price => {
    setEditingId(price.id)
    setEditValue(price.sale_price.amount)
    setEditDiscount(price.discount ?? '')
  }, [])

  const cancelEdit = useCallback(() => {
    setEditingId(null)
    setEditValue('')
    setEditDiscount('')
  }, [])

  const update = useCallback(() => {
    const body = {
      page,
      client_id:        clientId,
      sale_price_cents: Math.round(editValue * 100),
      discount:         editDiscount === '' ? 0 : Number(editDiscount)
    }
    setLoading(true)
    patchRequest({
      url: pathname({ path:`${dataPath}/${editingId}`, format:'json' }),
      body
    })
      .then(() => { cancelEdit(); load() })
      .catch(error => console.error('Error updating product-price', error))
  }, [clientId, dataPath, page, editingId, editValue, editDiscount, cancelEdit, load])


  // 6. delete
  const remove = useCallback(id => {
    deleteRequest(pathname({ path:`${dataPath}/${id}`, format:'json' }))
      .then(load)
      .catch(error => console.error('Error deleting product-price', error))
  }, [dataPath, load])


  return {
    // list state
    prices, loading,

    // add flow
    isAdding, template,
    startAdd, cancelAdd,
    loadTemplate, create,

    // edit flow
    editingId, editValue, editDiscount,
    startEdit, cancelEdit,
    setEditValue, setEditDiscount, update,

    // delete
    remove,
  }
}
